import { useLayoutEffect, useRef, useSyncExternalStore, type CSSProperties } from 'react';
import { getIqUiSnapshot, subscribeIqUi } from '../../state/iqUiStore.ts';
import { IQ_DOM } from './iqDomIds.ts';

function formatTime(seconds: number | null): string {
  if (seconds == null) return '0:00';
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

/** Самый мелкий кегль ответа относительно заданного: мельче на ТВ не прочесть. */
const MIN_ANSWER_SCALE = 0.6;

/**
 * Плашка ответа. Ширину и высоту присылает телефон, кегль — от высоты экрана;
 * длинный ответ («Файнтюнинг») в неё не влезал и обрезался. Текст переносится
 * на две строки по словам и ужимается шагами по 5%, пока не влезет, но не меньше
 * `MIN_ANSWER_SCALE`; слово разрывается, только если не влезло и так.
 */
function IqAnswerButton({ text, style }: { text: string; style: CSSProperties }) {
  const boxRef = useRef<HTMLDivElement>(null);
  const textRef = useRef<HTMLSpanElement>(null);
  useLayoutEffect(() => {
    const box = boxRef.current;
    const label = textRef.current;
    if (!box || !label) return;
    let scale = 1;
    label.style.fontSize = '1em';
    label.style.overflowWrap = 'normal';
    const fits = () => label.scrollWidth <= box.clientWidth && label.scrollHeight <= box.clientHeight;
    while (!fits() && scale > MIN_ANSWER_SCALE) {
      scale = Math.max(MIN_ANSWER_SCALE, scale - 0.05);
      label.style.fontSize = `${scale}em`;
    }
    // Слово длиннее плашки даже на минимальном кегле — тогда уже разрываем его.
    if (!fits()) label.style.overflowWrap = 'anywhere';
  }, [text, style.width, style.height, style.fontSize]);
  return (
    <div ref={boxRef} className="iq-ui__answer-btn" style={style}>
      <span className="iq-ui__answer-btn-shine" />
      <span ref={textRef} className="iq-ui__answer-btn-text">{text}</span>
    </div>
  );
}

export function IqHud() {
  const d = IQ_DOM;
  const s = useSyncExternalStore(subscribeIqUi, getIqUiSnapshot, getIqUiSnapshot);

  return (
    <div id={d.root} className="iq-ui" hidden={!s.hudVisible}>
      {/* Top HUD */}
      <div className="iq-ui__score-box">{s.score}</div>
      <div className="iq-ui__progress-pill">{s.progress}</div>
      <div className="iq-ui__timer-box">{formatTime(s.timeLeft)}</div>

      {/* Answer buttons — absolutely positioned from JSON x/y */}
      {s.answers.map((ans) => (
        <IqAnswerButton
          key={ans.id || ans.text}
          text={ans.text}
          style={{
            left: `${ans.x * 100}%`,
            top: `${ans.y * 100}%`,
            width: ans.w > 0 ? `${ans.w * 100}%` : undefined,
            height: ans.h > 0 ? `${ans.h * 100}vh` : undefined,
            backgroundColor: ans.backgroundColor,
            fontSize: `${s.answerTextSize * 100}vh`,
          }}
        />
      ))}

      {/* Bottom question panel */}
      {s.question ? (
        <div className="iq-ui__bottom-panel">
          <div className="iq-ui__question">{s.question}</div>
        </div>
      ) : null}
    </div>
  );
}
